import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
interface RegistrationPopupProps {
  isOpen: boolean;
  onClose: () => void;
}
// ============================================================================
// REGISTRO — TU diseño original + alta del contacto en Kit (ConvertKit)
// ============================================================================
// El formulario mantiene tu diseño. Al enviar, postea los datos al endpoint
// público del formulario de Kit mediante un <form> nativo dirigido a un iframe
// oculto (sin problemas de CORS), y luego redirige a tu página de la clase.
// Kit procesa el alta del lado servidor, agrega el suscriptor y dispara tu
// secuencia/automatización de bienvenida.
//
// �👇 PEGÁ AQUÍ EL ID DE TU FORMULARIO DE KIT (solo el número, ej: 8123456).
//    Lo encontrás en Kit → Grow → Landing Pages & Forms → tu formulario →
//    Embed → pestaña "HTML": en el atributo action="https://app.kit.com/forms/XXXXXXX/subscriptions".
const KIT_FORM_ID = 'REEMPLAZAR_CON_TU_FORM_ID';
const KIT_ACTION = `https://app.kit.com/forms/${KIT_FORM_ID}/subscriptions`;
const REDIRECT_URL = 'https://clase.tomasbenavidez.com/';
export function RegistrationPopup({
  isOpen,
  onClose
}: RegistrationPopupProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const validateEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim()) {
      setError('Por favor, completá todos los campos.');
      return;
    }
    if (!validateEmail(email)) {
      setError('Ingresá un correo electrónico válido.');
      return;
    }
    setIsSubmitting(true);
    // POST nativo del navegador a Kit (a través del iframe oculto, sin CORS).
    formRef.current?.submit();
    // Redirigimos a la clase.
    setTimeout(() => {
      window.location.href = REDIRECT_URL;
    }, 1200);
  };
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      clearTimeout(t);
    };
  }, [isOpen, isSubmitting, onClose]);
  return <AnimatePresence data-id="element-98">
      {isOpen && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onMouseDown={e => {
      if (e.target === e.currentTarget && !isSubmitting) onClose();
    }} data-id="element-99">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="registration-title" className="relative w-full max-w-md my-8 p-6 sm:p-8 bg-brand-dark border border-gray-800 rounded-2xl shadow-2xl" initial={{
        opacity: 0,
        scale: 0.95,
        y: 16
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 16
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 26
      }} data-id="element-100">
            <button type="button" onClick={onClose} disabled={isSubmitting} aria-label="Cerrar" className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange rounded disabled:opacity-50" data-id="element-101">
              <X className="w-6 h-6" data-id="element-102" />
            </button>

            <div className="text-center mb-6" data-id="element-103">
              <h3 id="registration-title" className="text-2xl font-bold text-white mb-2" data-id="element-104">
                Estás a un paso de acceder
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-105">
                Ingresá tus datos para recibir el acceso inmediato al curso
                gratuito.
              </p>
            </div>

            {/* iframe oculto: recibe la respuesta del POST sin recargar la página */}
            <iframe name="kit_target" title="Kit" className="hidden" aria-hidden="true" data-id="element-106" />

            <form ref={formRef} onSubmit={handleSubmit} action={KIT_ACTION} method="post" target="kit_target" acceptCharset="utf-8" className="space-y-4" noValidate data-id="element-107">
              <div data-id="element-108">
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-109">
                  Tu Nombre
                </label>
                <input ref={firstFieldRef} type="text" id="name" name="fields[first_name]" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-white placeholder-gray-500 transition-all" placeholder="Ej: Juan Pérez" disabled={isSubmitting} autoComplete="name" data-id="element-110" />
              </div>

              <div data-id="element-111">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1" data-id="element-112">
                  Tu Mejor Correo
                </label>
                <input type="email" id="email" name="email_address" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent text-white placeholder-gray-500 transition-all" placeholder="ejemplo@correo.com" disabled={isSubmitting} autoComplete="email" data-id="element-113" />
              </div>

              {error && <p role="alert" className="text-red-500 text-sm text-center" data-id="element-114">
                  {error}
                </p>}

              <button type="submit" disabled={isSubmitting} className="w-full py-4 px-6 mt-4 bg-brand-orange hover:bg-red-600 text-white font-bold rounded-lg transition-all transform hover:scale-[1.02] active:scale-95 flex items-center justify-center box-glow disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-dark focus-visible:ring-brand-orange" data-id="element-115">
                {isSubmitting ? <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" data-id="element-116" />
                    Procesando...
                  </> : '¡ACCEDER AL CURSO GRATIS!'}
              </button>

              <p className="text-xs text-center text-gray-500 mt-2" data-id="element-117">
                Tus datos están 100% seguros. Cero Spam.
              </p>
            </form>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}